import { Button, Card, Col, Divider, Flex, Form, Input, Radio, RadioChangeEvent, Row, Segmented, Tooltip, Typography } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// components
import PageTitle from 'components/title/title';
import BackButton from 'components/backButton/backButton';
import ComponentHeader from 'components/componentHeader/componentHeader';
import CompoundElement from 'components/compoundElement/compoundElement';
import DisabledElement from 'components/disabledElement/disabledElement';
import PhoneInputComponent from 'components/phoneInput/phoneInput';
import ActionCostComponent from 'modules/main/actions/cost';

// services

// styles

// models
import { NoticeCreatePayload, NoticeType } from '../models/notice';
import NotificationService from 'core/services/notification';
import { onCheckCompanyShareholder, onCreateNotice } from '../services/noticeService';
import { isRTL } from 'core/services/language';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'stores/store';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { subtractPoints } from 'stores/useCompanyStore';
import { CompanyMember, CompanyShareholder } from 'core/models/shareholder';
import { onGetServiceCost } from '../../commonServices';
import { ServiceCost } from '../../commonModels';

type FormFields = {
	message: string;
	notice_type: string;
	national_id: string;
	shareholder_name: string;
	shareholder_phone: string;
	shareholder_country_code: string;
};

interface NoticeCreatePageProps {}

const NoticeCreatePage: FunctionComponent<NoticeCreatePageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [noticeForm] = Form.useForm();

	const company = useSelector((state: RootState) => state.company);

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [generalServiceCost, setGeneralServiceCost] = useState<ServiceCost>();
	const [specificServiceCost, setSpecificServiceCost] = useState<ServiceCost>();
	const [selectedNoticeCost, setSelectedNoticeCost] = useState<ServiceCost>();

	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [isChecking, setIsChecking] = useState<boolean>(false);
	const [checked, setChecked] = useState<boolean>(false);
	const [noticeType, setNoticeType] = useState<NoticeType>('specific');

	const noticeTypeOptions = [
		{
			value: 'specific',
			label: t('ACTIONS.SPECIFIC'),
		},
		{
			value: 'general',
			label: t('ACTIONS.GENERAL'),
		},
	];

	useEffect(() => {
		getServiceCost();
	}, []);

	const getServiceCost = async () => {
		try {
			setIsLoading(true);

			const response = await Promise.all([onGetServiceCost('specific_notice'), onGetServiceCost('public_notice')]);

			setSpecificServiceCost(response[0]?.success);
			setGeneralServiceCost(response[1]?.success);

			setSelectedNoticeCost(response[0]?.success);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			navigate('/dashboard/actions/notice');
		}
	};

	const onCheckShareholder = async (nationalId: string) => {
		try {
			setIsChecking(true);

			const response = await onCheckCompanyShareholder(nationalId);

			if (response.type === 'Shareholder') {
				const shareholderData = response.data as CompanyShareholder;
				noticeForm.setFieldsValue({
					national_id: shareholderData.shareholder.national_id,
					shareholder_name: isRTL() ? shareholderData.shareholder.name : shareholderData.shareholder.name_en ?? shareholderData.shareholder.name,
					shareholder_phone: shareholderData.shareholder.phone_number,
					shareholder_country_code: shareholderData.shareholder.phone_country_code,
				});
			} else if (response.type === 'Member') {
				const memberData = response.data as CompanyMember;
				noticeForm.setFieldsValue({
					national_id: memberData.national_id,
					shareholder_name: isRTL() ? memberData.name : memberData.name_en ?? memberData.name,
					shareholder_phone: memberData.phone_number,
					shareholder_country_code: '20',
				});
			}

			setIsChecking(false);
			setChecked(true);
		} catch (error: any) {
			setIsChecking(false);

			if (error?.response?.data?.data?.errors?.national_id) {
				noticeForm.setFields([
					{
						name: 'national_id',
						errors: [error.response.data.data.errors.national_id],
					},
				]);

				return;
			}

			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const onChangeNoticeType = (value: string) => {
		setNoticeType(value as NoticeType);
		setChecked(false);
		setSelectedNoticeCost(value === 'general' ? generalServiceCost : specificServiceCost);

		noticeForm.setFieldsValue({
			national_id: '',
			shareholder_name: '',
			shareholder_phone: '',
			shareholder_country_code: '',
		});
	};

	const onFinish = async (values: FormFields) => {
		try {
			setIsSubmitting(true);

			const payload: NoticeCreatePayload = {
				notice_type: noticeType,
				message: values.message,
				national_id: noticeType === 'specific' ? values.national_id : undefined,
			};

			await onCreateNotice(payload);

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.NOTICE_CREATED'));
			dispatch(subtractPoints(selectedNoticeCost?.payment_method == 'points' ? selectedNoticeCost?.payment_details.points_required : 0));
			navigate('/dashboard/actions/notice');
			setIsSubmitting(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
			setIsSubmitting(false);
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			{isLoading ? (
				<LoadingComponent />
			) : (
				<Form form={noticeForm} onFinish={onFinish}>
					<Card>
						<ComponentHeader title={t('ACTIONS.NOTICE')} subtitle={t('ACTIONS.SEND_OFFICIAL_NOTICE')} />

						<Segmented
							className="custom white"
							onChange={onChangeNoticeType}
							options={noticeTypeOptions}
							block
							size="large"
							style={{ marginBottom: 30 }}
						/>

						<p>{noticeType === 'general' ? t('ACTIONS.SEND_NOTICE_TO_ALL_SHAREHOLDERS') : t('ACTIONS.SEND_NOTICE_TO_SPECIFIC_SHAREHOLDER')}</p>

						<Row gutter={[16, 16]}>
							<Col span={24}>
								<h5 className="mb-0">{t('ACTIONS.INVESTOR_MESSAGE')}</h5>
							</Col>

							{noticeType === 'specific' && (
								<>
									<Col span={24}>
										<Form.Item name="national_id" rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
											<CompoundElement
												buttonText={t('COMMON.CHECK')}
												onSubmit={onCheckShareholder}
												label={t('COMMON.NATIONAL_ID')}
												isLoading={isChecking}
												onChange={() => {
													noticeForm.setFieldsValue({
														national_id: '',
														shareholder_name: '',
														shareholder_phone: '',
														shareholder_country_code: '',
													});
													setChecked(false);
												}}
											/>
										</Form.Item>
									</Col>

									{checked && (
										<>
											<Col span={24} md={12}>
												<DisabledElement label={t('ACTIONS.SHAREHOLDER_NAME')} value={noticeForm.getFieldValue('shareholder_name')} />
											</Col>

											<Col span={24} md={12}>
												<PhoneInputComponent
													disabled
													userDialCode={noticeForm.getFieldValue('shareholder_country_code')}
													userPhoneNumber={noticeForm.getFieldValue('shareholder_phone')}
												/>
											</Col>
										</>
									)}
								</>
							)}

							<Col span={24}>
								<Typography style={{ fontSize: 15, marginBottom: 5 }}>{t('ACTIONS.MESSAGE')}</Typography>
								<Form.Item
									name="message"
									rules={[
										{ required: true, message: t('COMMON.FIELD_REQUIRED') },
										{
											min: 10,
											message: t('ACTIONS.MESSAGE_LENGTH_VALIDATION', { length: 10 }),
										},
									]}
									style={{ maxWidth: 'unset' }}>
									<Input.TextArea
										rows={5}
										style={{ resize: 'none' }}
										maxLength={210}
										count={{ show: true, max: 210 }}
										onBlur={(e) => {
											noticeForm.setFieldsValue({ message: e.target.value.trimEnd() });
										}}
									/>
								</Form.Item>
							</Col>

							<Col span={24}>
								<Flex justify="flex-end" align="center" gap={20} className="mt-5">
									<ActionCostComponent serviceCost={selectedNoticeCost!} />

									<Tooltip color="red" title={selectedNoticeCost?.can_access === false ? t('ACTIONS.INSUFFICIENT_BALANCE') : ''}>
										<Button type="primary" htmlType="submit" loading={isSubmitting} disabled={selectedNoticeCost?.can_access === false}>
											{t('COMMON.SEND')}
										</Button>
									</Tooltip>
								</Flex>
							</Col>
						</Row>
					</Card>
				</Form>
			)}
		</>
	);
};

export default NoticeCreatePage;
